import React from "react";
import InfoBanner from "./InfoBanner";

// ── Icons ─────────────────────────────────────────────────────────────────────

const HouseIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const PersonIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const BriefcaseIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

// ── Category configs ───────────────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  household: {
    title: "Household Info",
    description: "Confirm income details, investment strategy, and experience.",
    iconColor: "bg-red-100 text-red-600",
    borderColor: "border-red-300",
    Icon: HouseIcon,
  },
  personal: {
    title: "Personal Info",
    description: "Confirm personal, contact, employment details of household members.",
    iconColor: "bg-blue-100 text-blue-700",
    borderColor: "border-blue-300",
    Icon: PersonIcon,
  },
  accounts: {
    title: "Accounts Info",
    description:
      "Confirm details such as account transfers, contributions / withdrawals, and linked bank accounts.",
    iconColor: "bg-orange-100 text-orange-600",
    borderColor: "border-orange-300",
    Icon: BriefcaseIcon,
  },
};

// ── StatusBadge ────────────────────────────────────────────────────────────────

const StatusBadge = ({ isComplete }) =>
  isComplete ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 tracking-wide">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
      COMPLETE
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 tracking-wide">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      INCOMPLETE
    </span>
  );

// ── PendingItemsBox ──────────────────────────────────────────────────────────

const PendingItemsBox = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 min-h-[160px]">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
          Pending Items
        </p>
        <p className="text-sm text-gray-400 italic">No pending items.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 min-h-[160px]">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        Pending Items
      </p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.key} className="text-sm text-gray-700">
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const getPendingDisplayItems = (category, householdPending, membersPending, accountsPending) => {
  if (category === "household") {
    return (householdPending || []).map((f) => ({ key: f.key, label: f.label }));
  }

  if (category === "personal") {
    const seen = new Set();
    const items = [];
    (membersPending || []).forEach((member) => {
      (member.fields || []).forEach((field) => {
        if (!seen.has(field.key)) {
          seen.add(field.key);
          items.push({ key: field.key, label: field.label });
        }
      });
    });
    return items;
  }

  if (category === "accounts") {
    const seen = new Set();
    const items = [];
    (accountsPending || []).forEach((account) => {
      (account.fields || []).forEach((field) => {
        if (!seen.has(field.key)) {
          seen.add(field.key);
          items.push({ key: field.key, label: field.label });
        }
      });
    });
    return items;
  }

  return [];
};

// ── CategoryCard ───────────────────────────────────────────────────────────────

const CategoryCard = ({
  category,
  householdPending,
  membersPending,
  accountsPending,
  onComplete,
}) => {
  const config = CATEGORY_CONFIG[category];
  const { title, description, iconColor, borderColor, Icon } = config;

  const isComplete = (() => {
    if (category === "household") {
      return !householdPending || householdPending.length === 0;
    }
    if (category === "personal") {
      return (
        !membersPending ||
        membersPending.every((m) => !m.fields || m.fields.length === 0)
      );
    }
    if (category === "accounts") {
      return (
        !accountsPending ||
        accountsPending.every((a) => !a.fields || a.fields.length === 0)
      );
    }
    return true;
  })();

  return (
    <div className={`bg-white rounded-xl shadow-sm border-2 ${borderColor} p-6 flex flex-col min-h-[480px]`}>
      {/* Header row: icon + badge */}
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-lg ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <StatusBadge isComplete={isComplete} />
      </div>

      {/* Title + description */}
      <div className="mt-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
      </div>

      {/* Pending items */}
      <div className="flex-1 mt-6">
        <PendingItemsBox
          items={getPendingDisplayItems(category, householdPending, membersPending, accountsPending)}
        />
      </div>

      {/* Action button */}
      <button
        onClick={() => !isComplete && onComplete(category)}
        disabled={isComplete}
        className={`mt-auto w-full py-2 px-4 rounded-lg text-sm font-medium border transition-colors
          ${
            isComplete
              ? "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
              : "border-emerald-600 text-emerald-600 bg-white hover:bg-emerald-50 active:bg-emerald-100 cursor-pointer"
          }`}
      >
        {isComplete ? "All Complete" : "Complete"}
      </button>
    </div>
  );
};

// ── PendingDataOverview ────────────────────────────────────────────────────────

const PendingDataOverview = ({
  householdPending,
  membersPending,
  accountsPending,
  onComplete,
}) => {
  return (
    <div className="flex flex-col gap-8 min-h-[calc(100vh-200px)]">
      {/* Three-card grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <CategoryCard
          category="household"
          householdPending={householdPending}
          membersPending={membersPending}
          accountsPending={accountsPending}
          onComplete={onComplete}
        />
        <CategoryCard
          category="personal"
          householdPending={householdPending}
          membersPending={membersPending}
          accountsPending={accountsPending}
          onComplete={onComplete}
        />
        <CategoryCard
          category="accounts"
          householdPending={householdPending}
          membersPending={membersPending}
          accountsPending={accountsPending}
          onComplete={onComplete}
        />
      </div>

      {/* Info banner — pushed to bottom */}
      <div className="mt-auto">
        <InfoBanner />
      </div>
    </div>
  );
};

export default PendingDataOverview;
